import { useFindMany } from "@gadgetinc/react";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import { Button } from "../../externalComponents/tailwind/button";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import { Link } from "../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { Text } from "../../externalComponents/tailwind/text";
import { CustomerContext } from "../../providers";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { SegmentFiltersTooltip } from "../../externalComponents/SegmentFiltersTooltip";

export default function SendingsPage() {
  const { user } = useContext(CustomerContext);

  return (
    <>
      <PageHeading title="Sendings" />
      <OverflowFrame>
        <SendingList user={user} />
      </OverflowFrame>
    </>
  );
}

function SendingList({ user }) {
  const isMobile = useIsMobile();
  const [{ data: dataFuture, fetching: fetchingFuture }] = useFindMany(
    api.sending,
    {
      first: 250,
      filter: [
        {
          customerId: { equals: user.customerId },
        },
        {
          status: { equals: "registered" },
        },
      ],
      select: {
        id: true,
        contactList: true,
        sendingDate: true,
        contactListSegment: {
          externalId: true,
          type: true,
        },
        segment: {
          id: true,
          name: true,
        },
        segmentFilters: true,
        campaign: {
          name: true,
          trigger: true,
          id: true,
        },
      },
      sort: {
        sendingDate: "Ascending",
      },
    },
  );

  const [{ data: dataPast, fetching: fetchingPast }] = useFindMany(
    api.sending,
    {
      filter: {
        customerId: { equals: user.customerId },
        status: { equals: "completed" },
        isTest: { notEquals: true },
        campaign: {
          automationType: { isSet: false },
        },
      },
      select: {
        id: true,
        contactList: true,
        sendingDate: true,
        contactListSegment: {
          externalId: true,
          type: true,
        },
        segment: {
          id: true,
          name: true,
        },
        segmentFilters: true,
        campaign: {
          name: true,
          trigger: true,
          id: true,
        },
      },
      sort: {
        sendingDate: "Descending",
      },
    },
  );

  return (
    <>
      {(fetchingFuture || fetchingPast) && <LoadingSpinner />}
      {!fetchingFuture && !fetchingPast && (
        <PageMarkup
          dataFuture={dataFuture}
          dataPast={dataPast}
          isMobile={isMobile}
        ></PageMarkup>
      )}
    </>
  );
}

function PageMarkup({ dataFuture, dataPast, isMobile }) {
  const navigate = useNavigate();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const rowMarkupFuture = dataFuture?.map(
    (
      {
        id,
        contactList,
        sendingDate,
        campaign,
        contactListSegment,
        segment,
        segmentFilters,
      },
      index,
    ) => (
      <TableRow
        id={id}
        key={`future-${id}`}
        position={index}
        {...(!isMobile && {
          onClick: () => navigate(`/campaigns/edit/${campaign.id}`),
        })}
      >
        <>
          <TableCell className="font-bold">{campaign?.name}</TableCell>
          <TableCell>
            {sendingDate.toLocaleDateString("fr-FR", {
              weekday: "long",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            }) + ` [${timezone}]`}
          </TableCell>
          <TableCell className="text-center">{contactList.length}</TableCell>
          <TableCell className="w-[30%]">
            {segment ? (
              <SegmentFiltersTooltip segmentFilters={segmentFilters}>
                <span className="cursor-help">{segment.name}</span>
              </SegmentFiltersTooltip>
            ) : contactListSegment?.type === "klaviyo-segment" ? (
              <Link
                style={{
                  wordBreak: "break-all",
                  overflowWrap: "break-word",
                }}
                className="hover:underline"
                href={`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
                target="_blank"
              >
                {`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
              </Link>
            ) : (
              "-"
            )}
          </TableCell>
          {!isMobile && (
            <TableCell className="text-center">
              <Button
                outline
                onClick={() => {
                  api.sending.update(id, {
                    status: "canceled",
                  });
                }}
              >
                <XMarkIcon />
              </Button>
            </TableCell>
          )}
        </>
      </TableRow>
    ),
  );

  const rowMarkupPast = dataPast?.map(
    (
      {
        id,
        contactList,
        sendingDate,
        campaign,
        contactListSegment,
        segment,
        segmentFilters,
      },
      index,
    ) => (
      <TableRow
        id={id}
        key={`past-${id}`}
        position={index}
        {...(!isMobile && {
          onClick: () => navigate(`/campaigns/edit/${campaign.id}`),
        })}
      >
        <TableCell>{campaign?.name}</TableCell>
        <TableCell>
          {sendingDate.toLocaleDateString("fr-FR", {
            weekday: "long",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          }) + ` [${timezone}]`}
        </TableCell>
        <TableCell className="text-center">{contactList.length}</TableCell>
        <TableCell className="w-[30%]">
          {segment ? (
            <SegmentFiltersTooltip segmentFilters={segmentFilters}>
              <span className="cursor-help">{segment.name}</span>
            </SegmentFiltersTooltip>
          ) : contactListSegment?.type === "klaviyo-segment" ? (
            <Link
              style={{
                wordBreak: "break-all",
                overflowWrap: "break-word",
              }}
              className="hover:underline"
              href={`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
              target="_blank"
            >
              {`https://www.klaviyo.com/list/${contactListSegment?.externalId}`}
            </Link>
          ) : (
            "-"
          )}
        </TableCell>
      </TableRow>
    ),
  );

  return (
    <div className="mx-3 md:mx-0">
      <br />
      <Text className="text-xl font-bold">Scheduled sendings</Text>
      <br />
      {dataFuture?.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Campaign name</TableHeader>
              <TableHeader>Sending date</TableHeader>
              <TableHeader className="text-center">Recipients</TableHeader>
              <TableHeader className="w-[30%]">Segment</TableHeader>
              {!isMobile && (
                <TableHeader className="text-center">Cancel</TableHeader>
              )}
            </TableRow>
          </TableHead>
          <TableBody>{rowMarkupFuture}</TableBody>
        </Table>
      ) : (
        <EmptyState
          heading="There are no scheduled campaigns"
          action={() => navigate("/campaigns")}
          icon={<ChevronLeftIcon />}
          actionText="Back"
          img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      )}

      <br />
      <Text className="text-xl font-bold">Past sendings</Text>

      <br />

      {dataPast?.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Campaign name</TableHeader>
              <TableHeader>Sending date</TableHeader>
              <TableHeader className="text-center">Recipients</TableHeader>
              <TableHeader className="w-[30%]">Segment</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>{rowMarkupPast}</TableBody>
        </Table>
      ) : (
        <EmptyState
          heading="No sending has been made"
          action={() => navigate("/campaigns")}
          icon={<ChevronLeftIcon />}
          actionText="Back"
          img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      )}
      <br />
    </div>
  );
}
